import { IconTable } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { FlatTableColumn } from './tableData';
import { EMPTY_CELL, formatCell, isNumericColumn } from './tableData';
import type { TableWidgetRow } from '../widgetData';
import { componentsTestIds } from '@components/testids';
import { WidgetEmptyState } from './ReadmodelWidgetStates';
import classes from './WidgetTable.module.css';

/**
 * TV1-1929 (S14) — the generic Table/Pivot widget renderer.
 *
 * Plain Mantine `Table` inside a `ScrollArea` — deliberately NO new dependency
 * (there is no `mantine-react-table` / `@tanstack/react-table` / ag-grid in this
 * app, and a table primitive does not justify introducing one). Shape copied
 * from `CriticalStockTable` (TV1-1933), not imported: that one's column set is
 * fixed, this one's is whatever Cube's `tableColumns()` says.
 *
 * **Pivot is not a mode here.** The rows/columns arrive already pivoted from
 * `extractWidgetData` (`tablePivot(pivotConfig)` / `tableColumns(pivotConfig)`),
 * so this component renders a flat table and a pivot with the exact same code —
 * a pivot just arrives with more (nested) columns.
 *
 * **No header sorting in v1, on purpose.** Order is configured in the builder's
 * OrderBy section and emitted to Cube, so the SERVER order is authoritative.
 * Client-side re-sorting would be silently WRONG whenever `limit` is set: you'd
 * be re-sorting a server-side top-N and presenting it as the top-N of the new
 * ordering. If sorting is ever added it must re-query by mutating
 * `definition.orderBy`.
 */

export type TableWidgetProps = {
  /** Already pivoted, already capped (see `capTableRows`). */
  rows: TableWidgetRow[];
  /** Already flattened to leaves + selected (see `flattenTableColumns`). */
  columns: FlatTableColumn[];
  /** Sum over the RENDERED rows; present only when `showTotals` is on. */
  totals?: TableWidgetRow | null;
  /** `true` when the row cap bit — the totals then cover only what is shown. */
  truncated?: boolean;
  /** Explicit locale for cell formatting; defaults to the browser locale. */
  locale?: string;
};

export const TableWidget = ({ rows, columns, totals, truncated, locale }: TableWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  // An empty Cube result is `[]`, which is TRUTHY — so it sails past the
  // `if (!data) return <Skeleton/>` guard in WebWidget and genuinely lands here.
  // The empty state is this component's job.
  if ( !columns.length || !rows.length ) {
    /*
     * The two reasons a table is empty are DIFFERENT problems and now say so.
     * "No columns" is a card that was never finished; "no rows" is a query that ran
     * and matched nothing. They used to share one line ("No data for this query."),
     * which sent an author looking at their filter when the actual fix was to pick
     * a measure.
     */
    return !columns.length
      ? (
        <WidgetEmptyState
          icon={<IconTable size={20} />}
          title={t( 'widget_table_no_columns_title', 'Falta elegir columnas' )}
          description={t(
            'widget_table_no_columns',
            'Agregá al menos una medida o dimensión para que la tabla tenga qué mostrar.'
          )}
          testId={componentsTestIds.tableWidgetEmpty}
        />
      )
      : (
        <WidgetEmptyState
          icon={<IconTable size={20} />}
          title={t( 'widget_table_empty_title', 'Sin resultados' )}
          description={t(
            'widget_table_empty',
            'La consulta corrió y no devolvió filas. Revisá el período y los filtros de la tarjeta.'
          )}
          testId={componentsTestIds.tableWidgetEmpty}
        />
      );
  }

  return (
    <div className={classes.scroller}>
      <table className={classes.table} data-testid={componentsTestIds.tableWidget}>
        <thead>
          <tr>
            {columns.map(( column ) => (
              <th
                key={column.dataIndex}
                className={isNumericColumn( column ) ? classes.numeric : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(( row, rowIndex ) => (
            // Cube rows carry no stable id — the row's position in the
            // server-ordered result IS its identity.
            <tr key={rowIndex} data-testid={componentsTestIds.tableWidgetRow}>
              {columns.map(( column ) => (
                <td
                  key={column.dataIndex}
                  className={isNumericColumn( column ) ? classes.numeric : undefined}
                >
                  {formatCell( row[column.dataIndex], column, locale )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {totals ? (
          <tfoot>
            <tr className={classes.totalsRow} data-testid={componentsTestIds.tableWidgetTotals}>
              {columns.map(( column, index ) => {
                // Only NUMERIC columns have a total; a dimension column has none,
                // so the first one carries the label instead.
                const value = totals[column.dataIndex];
                const isLabelCell = index === 0 && value === undefined;

                return (
                  <td
                    key={column.dataIndex}
                    className={isNumericColumn( column ) ? classes.numeric : undefined}
                  >
                    {isLabelCell
                      // The totals are summed over the RENDERED rows. If the cap
                      // bit, say so — a partial sum labelled "Total" is a lie.
                      ? truncated
                        ? t( 'widget_table_totals_truncated', 'Total (shown rows)' )
                        : t( 'widget_table_totals', 'Total' )
                      : value === undefined ? EMPTY_CELL : formatCell( value, column, locale )}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
};
